import React, { useState, useMemo, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { useAuth } from '../context/AuthContext';
import BarCard from '../components/BarCard';
import LoadingSpinner from '../components/LoadingSpinner';
import FoundingDiverBanner from '../components/FoundingDiverBanner';
import { Search, Filter, SortDesc, MapPin, X, Star, LogIn, PlusCircle, EyeOff, Camera, CheckCircle2 } from 'lucide-react';
import { trackEvent } from '../lib/posthog';
import { useLocationScope } from '../context/LocationContext';
import MetroSwitcher from '../components/MetroSwitcher';
import FeatureFilterChips, { FilterChipList } from '../components/FeatureFilterChips';
import NearbyCheckInButton from '../components/NearbyCheckInButton';
import { filterBarsByAreas, filterBarsByFeatures, getAreaFilterOptions } from '../utils/barFilters';
import { getBarLocationLabel } from '../utils/barLocation';
import { getFeaturesForMetro } from '../data/features';

function ListView() {
  const { bars, ratings, getBarStats, getBarThumbnail, loading } = useApp();
  const { user } = useAuth();
  const { getScopedBars, showAreaFilter, areaLabel, areas, activeMetro, nearMeSelected } = useLocationScope();
  const pageTitle = nearMeSelected ? 'Dive Bars Near You' : `${activeMetro?.name || 'Chicago'} Dive Bars`;
  const scopedBars = useMemo(() => getScopedBars(bars), [bars, getScopedBars]);
  const [selectedFeatures, setSelectedFeatures] = useState([]);
  
  // Tab state: 'my-bars' or 'all-bars'
  const [activeTab, setActiveTab] = useState(() => {
    // Default to My Bars if user is logged in, otherwise All Bars
    const saved = localStorage.getItem('tilted-bars-tab');
    return saved || (user ? 'my-bars' : 'all-bars');
  });
  
  const [search, setSearch] = useState('');
  const [selectedNeighborhoods, setSelectedNeighborhoods] = useState([]);
  const [sortBy, setSortBy] = useState('rating');
  const [showFilters, setShowFilters] = useState(false);
  
  // All Bars tab filters
  const [diveOnly, setDiveOnly] = useState(() => {
    const saved = localStorage.getItem('tilted-filter-dive');
    return saved === 'true';
  });
  const [highlightRated, setHighlightRated] = useState(() => {
    const saved = localStorage.getItem('tilted-filter-highlight-rated');
    return saved === 'true';
  });
  const [notTriedOnly, setNotTriedOnly] = useState(() => {
    const saved = localStorage.getItem('tilted-filter-not-tried');
    return saved === 'true';
  });
  const [showLoginPrompt, setShowLoginPrompt] = useState(false);

  const lastMetroRef = useRef(activeMetro?.id);
  useEffect(() => {
    if (lastMetroRef.current === activeMetro?.id) return;
    lastMetroRef.current = activeMetro?.id;
    setSelectedNeighborhoods([]);
    setSelectedFeatures([]);
    setSearch('');
    if (user) setActiveTab('all-bars');
  }, [activeMetro?.id, user]);

  // Save tab state to localStorage
  useEffect(() => {
    localStorage.setItem('tilted-bars-tab', activeTab);
  }, [activeTab]);

  // Track search with debounce
  useEffect(() => {
    if (!search.trim()) return;
    const timer = setTimeout(() => {
      trackEvent('search_performed', { search_term: search.trim() });
    }, 500);
    return () => clearTimeout(timer);
  }, [search]);

  // Save filter states to localStorage
  useEffect(() => {
    localStorage.setItem('tilted-filter-dive', diveOnly.toString());
  }, [diveOnly]);

  useEffect(() => {
    localStorage.setItem('tilted-filter-highlight-rated', highlightRated.toString());
  }, [highlightRated]);

  useEffect(() => {
    localStorage.setItem('tilted-filter-not-tried', notTriedOnly.toString());
  }, [notTriedOnly]);

  // Get user's rated bar IDs and ratings
  const userRatings = useMemo(() => {
    if (!user) return [];
    return ratings.filter(r => r.userId === user.id);
  }, [user, ratings]);

  const userRatedBarIds = useMemo(() => {
    return new Set(userRatings.map(r => r.barId));
  }, [userRatings]);

  const hasUserRated = (barId) => userRatedBarIds.has(barId);
  
  const getUserRating = (barId) => {
    return userRatings.find(r => r.barId === barId);
  };

  // My Bars filters
  const [myBarsDiveOnly, setMyBarsDiveOnly] = useState(false);
  const [myBarsSortBy, setMyBarsSortBy] = useState('recent');

  // My Bars tab: Only bars I've rated, with filters
  const myRatedBars = useMemo(() => {
    if (!user) return [];
    
    // Get bars I've rated with my rating data
    let ratedBars = userRatings
      .map(rating => {
        const bar = scopedBars.find(b => b.id === rating.barId);
        if (!bar) return null;
        const stats = getBarStats(bar.id);
        return { ...bar, myRating: rating, stats };
      })
      .filter(Boolean);
    
    // Apply search filter
    if (search) {
      const searchLower = search.toLowerCase();
      ratedBars = ratedBars.filter(bar =>
        bar.name.toLowerCase().includes(searchLower) ||
        bar.neighborhood.toLowerCase().includes(searchLower) ||
        getBarLocationLabel(bar).toLowerCase().includes(searchLower)
      );
    }

    if (selectedNeighborhoods.length > 0) {
      ratedBars = filterBarsByAreas(ratedBars, selectedNeighborhoods, activeMetro);
    }
    if (selectedFeatures.length > 0) {
      ratedBars = filterBarsByFeatures(ratedBars, selectedFeatures);
    }

    // Apply dive bar filter
    if (myBarsDiveOnly) {
      ratedBars = ratedBars.filter(bar => bar.stats.divePercentage > 50);
    }

    // Apply sorting
    ratedBars.sort((a, b) => {
      switch (myBarsSortBy) {
        case 'recent':
          return b.myRating.createdAt - a.myRating.createdAt;
        case 'myRating':
          return b.myRating.overall - a.myRating.overall;
        case 'communityRating':
          return b.stats.overall - a.stats.overall;
        case 'name':
          return a.name.localeCompare(b.name);
        case 'diveScore':
          return b.stats.divePercentage - a.stats.divePercentage;
        default:
          return b.myRating.createdAt - a.myRating.createdAt;
      }
    });
    
    return ratedBars;
  }, [user, userRatings, scopedBars, search, selectedNeighborhoods, selectedFeatures, myBarsDiveOnly, myBarsSortBy, getBarStats]);

  // All Bars tab: All bars with filters
  const allFilteredBars = useMemo(() => {
    let result = [...scopedBars];

    // Search filter
    if (search) {
      const searchLower = search.toLowerCase();
      result = result.filter(bar =>
        bar.name.toLowerCase().includes(searchLower) ||
        bar.neighborhood.toLowerCase().includes(searchLower) ||
        getBarLocationLabel(bar).toLowerCase().includes(searchLower)
      );
    }

    if (selectedNeighborhoods.length > 0) {
      result = filterBarsByAreas(result, selectedNeighborhoods, activeMetro);
    }
    if (selectedFeatures.length > 0) {
      result = filterBarsByFeatures(result, selectedFeatures);
    }

    // Dive bar filter
    if (diveOnly) {
      result = result.filter(bar => {
        const stats = getBarStats(bar.id);
        return stats.divePercentage > 50;
      });
    }

    // Not tried filter (bars I haven't rated)
    if (notTriedOnly && user) {
      result = result.filter(bar => !hasUserRated(bar.id));
    }

    // Sorting
    result.sort((a, b) => {
      const statsA = getBarStats(a.id);
      const statsB = getBarStats(b.id);

      switch (sortBy) {
        case 'rating':
          return statsB.overall - statsA.overall;
        case 'diveScore':
          return statsB.divePercentage - statsA.divePercentage;
        case 'name':
          return a.name.localeCompare(b.name);
        case 'price':
          return statsB.price - statsA.price;
        case 'reviews':
          return statsB.totalRatings - statsA.totalRatings;
        default:
          return 0;
      }
    });

    return result;
  }, [scopedBars, search, selectedNeighborhoods, selectedFeatures, sortBy, diveOnly, notTriedOnly, user, getBarStats, hasUserRated]);

  const toggleNeighborhood = (neighborhood) => {
    setSelectedNeighborhoods(prev =>
      prev.includes(neighborhood)
        ? prev.filter(n => n !== neighborhood)
        : [...prev, neighborhood]
    );
  };

  const clearFilters = () => {
    setSelectedNeighborhoods([]);
    setSelectedFeatures([]);
    setDiveOnly(false);
    setHighlightRated(false);
    setNotTriedOnly(false);
    setSearch('');
  };

  const handleHighlightRatedToggle = () => {
    if (!user) {
      setShowLoginPrompt(true);
      setTimeout(() => setShowLoginPrompt(false), 3000);
      return;
    }
    setHighlightRated(!highlightRated);
  };

  const handleNotTriedToggle = () => {
    if (!user) {
      setShowLoginPrompt(true);
      setTimeout(() => setShowLoginPrompt(false), 3000);
      return;
    }
    setNotTriedOnly(!notTriedOnly);
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  const activeFiltersCount = selectedNeighborhoods.length + selectedFeatures.length + (diveOnly ? 1 : 0) + (notTriedOnly ? 1 : 0);
  const areaOptions = getAreaFilterOptions(areas, scopedBars, activeMetro);
  const displayedBars = activeTab === 'my-bars' ? myRatedBars : allFilteredBars;


  return (
    <div className="min-h-screen bg-dive-black pb-24">
      {/* Sticky Header: Title + Add Bar + Tabs + Search */}
      <div 
        className="sticky z-40 bg-dive-black/95 backdrop-blur-sm border-b border-dive-gray"
        style={{ top: 'var(--app-top-offset)' }}
      >
        <div className="max-w-4xl mx-auto px-4 pt-4 pb-3">
          {/* Title + location pill + Add Bar */}
          <div className="flex items-start md:items-center justify-between gap-3 mb-3">
            <div className="flex items-start md:items-center gap-3 min-w-0">
              <img src="/logo-icon.png" alt="Tilted" className="w-8 h-8 object-contain md:hidden flex-shrink-0" />
              <img src="/logo-nameplate.png" alt="Tilted" className="h-8 w-auto object-contain hidden md:block flex-shrink-0" />
              <div className="min-w-0 md:flex md:items-center md:gap-3">
                <h1 className="font-display text-2xl leading-8 text-white tracking-wide uppercase">
                  {pageTitle}
                </h1>
                <MetroSwitcher variant="pill" className="mt-1 md:mt-0" />
              </div>
            </div>
            
            <div className="flex items-center gap-2 flex-shrink-0">
            {user && <NearbyCheckInButton variant="icon" />}
            {user ? (
              <Link
                to="/add"
                className="flex items-center gap-2 px-3 py-2 bg-dive-green/20 border border-dive-green text-dive-green rounded-lg hover:bg-dive-green/30 transition-all"
              >
                <PlusCircle className="w-4 h-4" />
                <span className="text-sm font-medium hidden sm:inline">Add Bar</span>
              </Link>
            ) : (
              <Link
                to="/auth"
                className="flex items-center gap-2 px-3 py-2 bg-dive-gray/50 border border-dive-gray text-gray-400 rounded-lg hover:border-dive-light transition-all"
              >
                <LogIn className="w-4 h-4" />
                <span className="text-sm font-medium hidden sm:inline">Login to Add</span>
              </Link>
            )}
            </div>
          </div>

          {/* Tab Toggle */}
          <div className="flex bg-dive-darker rounded-lg p-1 mb-3">
            <button
              onClick={() => setActiveTab('my-bars')}
              disabled={!user}
              className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-all flex items-center justify-center gap-2 ${
                activeTab === 'my-bars'
                  ? 'bg-amber-500 text-white'
                  : user 
                    ? 'text-gray-400 hover:text-white'
                    : 'text-gray-600 cursor-not-allowed'
              }`}
            >
              <Star className="w-4 h-4" />
              My Bars
              {user && userRatings.length > 0 && (
                <span className={`text-xs px-1.5 rounded-full ${
                  activeTab === 'my-bars' ? 'bg-white/20' : 'bg-amber-500/20 text-amber-400'
                }`}>
                  {userRatings.length}
                </span>
              )}
            </button>
            <button
              onClick={() => setActiveTab('all-bars')}
              className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-all flex items-center justify-center gap-2 ${
                activeTab === 'all-bars'
                  ? 'bg-dive-neon text-white'
                  : 'text-gray-400 hover:text-white'
              }`}
            >
              All Bars
            </button>
          </div>

          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
            <input
              type="text"
              placeholder={activeTab === 'my-bars' ? "Search your rated bars..." : "Search bars or neighborhoods..."}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 bg-dive-dark border border-dive-gray rounded-lg text-white placeholder-gray-500"
            />
          </div>
        </div>
      </div>

      {/* Non-sticky: Filters + Sort (scrolls with content) */}
      <div className="max-w-4xl mx-auto px-4 pt-3 pb-4">
        {/* Bar count */}
        <p className="text-gray-400 text-xs mb-2">
          {activeTab === 'my-bars' 
            ? `${myRatedBars.length} bars rated`
            : `Showing ${displayedBars.length} of ${scopedBars.length} bars`
          }
        </p>

        {/* My Bars Tab: Filter and Sort Controls */}
        {activeTab === 'my-bars' && user && (
          <div className="flex gap-2 flex-nowrap sm:flex-wrap overflow-x-auto sm:overflow-visible scrollbar-hide -mx-4 px-4 sm:mx-0 sm:px-0">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`flex shrink-0 whitespace-nowrap items-center gap-2 px-3 py-2 rounded-lg border transition-all ${
                showFilters || selectedNeighborhoods.length > 0
                  ? 'bg-amber-500/20 border-amber-500 text-amber-400'
                  : 'border-dive-gray text-gray-400 hover:border-dive-light'
              }`}
            >
              <Filter className="w-4 h-4" />
              <span className="text-sm">Filter</span>
              {selectedNeighborhoods.length > 0 && (
                <span className="bg-amber-500 text-white text-xs px-1.5 py-0.5 rounded-full">
                  {selectedNeighborhoods.length}
                </span>
              )}
            </button>

            {/* Dive Bars Only */}
            <button
              onClick={() => setMyBarsDiveOnly(!myBarsDiveOnly)}
              className={`flex shrink-0 whitespace-nowrap items-center gap-2 px-3 py-2 rounded-lg border transition-all ${
                myBarsDiveOnly
                  ? 'bg-dive-neon/20 border-dive-neon text-white'
                  : 'border-dive-gray text-gray-400 hover:border-dive-light'
              }`}
            >
              <span className="text-sm">Dive Bars Only</span>
            </button>

            <div className="flex shrink-0 items-center gap-2 ml-auto">
              <SortDesc className="w-4 h-4 text-gray-500" />
              <select
                value={myBarsSortBy}
                onChange={(e) => setMyBarsSortBy(e.target.value)}
                className="bg-dive-dark border border-dive-gray rounded-lg px-2 py-2 text-sm text-white"
              >
                <option value="recent">Recently Rated</option>
                <option value="myRating">My Top Rated</option>
                <option value="communityRating">Community Rating</option>
                <option value="diveScore">Dive Score</option>
                <option value="name">Name A-Z</option>
              </select>
            </div>
          </div>
        )}

        {/* All Bars Tab: Filter and Sort Controls */}
        {activeTab === 'all-bars' && (
          <div className="flex gap-2 flex-nowrap sm:flex-wrap overflow-x-auto sm:overflow-visible scrollbar-hide -mx-4 px-4 sm:mx-0 sm:px-0">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`flex shrink-0 whitespace-nowrap items-center gap-2 px-3 py-2 rounded-lg border transition-all ${
                showFilters || selectedNeighborhoods.length > 0
                  ? 'bg-dive-neon/20 border-dive-neon text-white'
                  : 'border-dive-gray text-gray-400 hover:border-dive-light'
              }`}
            >
              <Filter className="w-4 h-4" />
              <span className="text-sm">Filter</span>
              {selectedNeighborhoods.length > 0 && (
                <span className="bg-dive-neon text-white text-xs px-1.5 py-0.5 rounded-full">
                  {selectedNeighborhoods.length}
                </span>
              )}
            </button>

            {/* Dive Bars Only */}
            <button
              onClick={() => setDiveOnly(!diveOnly)}
              className={`flex shrink-0 whitespace-nowrap items-center gap-2 px-3 py-2 rounded-lg border transition-all ${
                diveOnly
                  ? 'bg-dive-neon/20 border-dive-neon text-white'
                  : 'border-dive-gray text-gray-400 hover:border-dive-light'
              }`}
            >
              <span className="text-sm">Dive Bars Only</span>
            </button>

            {/* My Rated Bars (Highlight) */}
            <button
              onClick={handleHighlightRatedToggle}
              className={`flex shrink-0 whitespace-nowrap items-center gap-2 px-3 py-2 rounded-lg border transition-all ${
                highlightRated && user
                  ? 'bg-amber-500/20 border-amber-500 text-amber-400'
                  : user
                    ? 'border-dive-gray text-gray-400 hover:border-amber-500/50 hover:text-amber-400'
                    : 'border-dive-gray text-gray-600 cursor-not-allowed'
              }`}
              title={user ? "Highlight bars you've rated" : "Log in to use this filter"}
            >
              <Star className="w-4 h-4" />
              <span className="text-sm">My Rated</span>
            </button>

            {/* Have Not Tried */}
            <button
              onClick={handleNotTriedToggle}
              className={`flex shrink-0 whitespace-nowrap items-center gap-2 px-3 py-2 rounded-lg border transition-all ${
                notTriedOnly && user
                  ? 'bg-gray-500/20 border-gray-400 text-gray-300'
                  : user
                    ? 'border-dive-gray text-gray-400 hover:border-gray-400'
                    : 'border-dive-gray text-gray-600 cursor-not-allowed'
              }`}
              title={user ? "Show only bars you haven't tried" : "Log in to use this filter"}
            >
              <EyeOff className="w-4 h-4" />
              <span className="text-sm">Not Tried</span>
            </button>

            <div className="flex shrink-0 items-center gap-2 ml-auto">
              <SortDesc className="w-4 h-4 text-gray-500" />
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="bg-dive-dark border border-dive-gray rounded-lg px-2 py-2 text-sm text-white"
              >
                <option value="rating">Top Rated</option>
                <option value="diveScore">Dive Score</option>
                <option value="price">Best Value</option>
                <option value="reviews">Most Reviewed</option>
                <option value="name">Name A-Z</option>
              </select>
            </div>
          </div>
        )}

        {/* Login Prompt */}
        {showLoginPrompt && (
          <div className="mt-3 bg-amber-500/20 border border-amber-500 rounded-lg px-4 py-2 flex items-center gap-2 text-amber-400">
            <LogIn className="w-4 h-4" />
            <span className="text-sm">Log in to use this feature</span>
          </div>
        )}
      </div>

      {/* Filters Panel (Both tabs) */}
      {showFilters && (
        <div className="bg-dive-darker border-b border-dive-gray">
          <div className="max-w-4xl mx-auto p-4">
            <div className="mb-4">
              <p className="text-gray-400 text-xs mb-2">Features</p>
              <FeatureFilterChips
                metroId={activeMetro?.id}
                selectedFeatures={selectedFeatures}
                onToggle={(key) => setSelectedFeatures((prev) =>
                  prev.includes(key) ? prev.filter((item) => item !== key) : [...prev, key]
                )}
              />
            </div>
            <div className="mb-4">
              <div className="flex items-center justify-between mb-2">
                <p className="text-gray-400 text-xs">{showAreaFilter ? `${areaLabel}s` : 'Area'}</p>
                {activeFiltersCount > 0 && (
                  <button
                    onClick={clearFilters}
                    className="flex items-center gap-1 text-gray-400 text-sm hover:text-white"
                  >
                    <X className="w-3 h-3" />
                    Clear all
                  </button>
                )}
              </div>
              {showAreaFilter ? (
                <FilterChipList
                  items={areaOptions}
                  selected={selectedNeighborhoods}
                  onToggle={toggleNeighborhood}
                />
              ) : (
                <p className="text-gray-500 text-sm">Area filter hides outside large cities.</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Bar List */}
      <div className="max-w-4xl mx-auto p-4">
        {/* Founding Diver Badge Banner */}
        {user && <FoundingDiverBanner userId={user.id} />}
        
        {/* My Bars Tab: Not logged in */}
        {activeTab === 'my-bars' && !user && (
          <div className="text-center py-12">
            <LogIn className="w-16 h-16 text-dive-gray mx-auto mb-4" />
            <h3 className="text-white text-lg mb-2">Sign in to see your bars</h3>
            <p className="text-gray-400 text-sm mb-4">
              Track and view all the bars you've rated
            </p>
            <Link to="/auth" className="btn-primary">
              Sign In
            </Link>
          </div>
        )}

        {/* My Bars Tab: No ratings yet */}
        {activeTab === 'my-bars' && user && myRatedBars.length === 0 && !search && (
          <div className="text-center py-12">
            <Star className="w-16 h-16 text-amber-500/30 mx-auto mb-4" />
            <h3 className="text-white text-lg mb-2">No bars rated yet</h3>
            <p className="text-gray-400 text-sm mb-4">
              Start exploring {activeMetro?.name || 'your city'}'s dive bars and rate your favorites!
            </p>
            <button
              onClick={() => setActiveTab('all-bars')}
              className="btn-primary"
            >
              Browse All Bars
            </button>
          </div>
        )}

        {/* My Bars Tab: Search with no results */}
        {activeTab === 'my-bars' && user && myRatedBars.length === 0 && search && (
          <div className="text-center py-12">
            <Search className="w-16 h-16 text-dive-gray mx-auto mb-4" />
            <h3 className="text-white text-lg mb-2">No matches found</h3>
            <p className="text-gray-400 text-sm mb-4">
              No rated bars match "{search}"
            </p>
            <button
              onClick={() => setSearch('')}
              className="btn-secondary"
            >
              Clear Search
            </button>
          </div>
        )}

        {/* My Bars Tab: Show rated bars */}
        {activeTab === 'my-bars' && user && myRatedBars.length > 0 && (
          <div className="grid gap-4">
            {myRatedBars.map(bar => {
              const isDive = bar.stats.divePercentage > 50;
              const thumbnail = getBarThumbnail ? getBarThumbnail(bar.id) : null;
              
              return (
                <Link
                  key={bar.id}
                  to={`/bar/${bar.id}`}
                  className="block bg-dive-dark border border-dive-gray rounded-xl overflow-hidden hover:border-amber-500/50 transition-all"
                >
                  <div className="flex">
                    {/* Photo placeholder */}
                    <div className="w-24 h-24 sm:w-32 sm:h-32 flex-shrink-0 bg-dive-darker relative">
                      {thumbnail ? (
                        <img
                          src={thumbnail.url}
                          alt={bar.name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Camera className="w-8 h-8 text-gray-700" />
                        </div>
                      )}
                      {/* Dive badge on photo */}
                      {isDive && (
                        <div className="absolute top-2 left-2">
                          <span className="flex items-center gap-1 bg-dive-neon/90 text-white text-[10px] px-1.5 py-0.5 rounded-full">
                            <CheckCircle2 className="w-3 h-3" />
                            Dive
                          </span>
                        </div>
                      )}
                    </div>
                    
                    {/* Content */}
                    <div className="flex-1 p-3 sm:p-4 flex flex-col justify-between">
                      <div>
                        <div className="flex items-start justify-between mb-1">
                          <h3 className="font-display text-base sm:text-lg text-white leading-tight">{bar.name}</h3>
                          <span className="text-gray-500 text-xs flex-shrink-0 ml-2">
                            {new Date(bar.myRating.createdAt).toLocaleDateString()}
                          </span>
                        </div>
                        <p className="text-gray-500 text-sm">{getBarLocationLabel(bar)}</p>
                      </div>
                      
                      {/* Ratings - Clearer separation */}
                      <div className="flex items-center gap-4 mt-2">
                        {/* My Rating - Prominent */}
                        <div className="flex items-center gap-2 bg-amber-500/10 border border-amber-500/30 rounded-lg px-3 py-1.5">
                          <Star className="w-5 h-5 fill-amber-400 text-amber-400" />
                          <div className="flex flex-col">
                            <span className="text-amber-400 font-bold text-lg leading-none">{bar.myRating.overall.toFixed(1)}</span>
                            <span className="text-amber-400/70 text-[10px] uppercase tracking-wide">Your Rating</span>
                          </div>
                        </div>
                        
                        {/* Community Rating - Secondary */}
                        <div className="flex flex-col text-center">
                          <span className="text-gray-400 font-medium text-base">{bar.stats.overall.toFixed(1)}</span>
                          <span className="text-gray-600 text-[10px] uppercase tracking-wide">Community</span>
                        </div>
                        
                        {/* Dive percentage */}
                        <div className="flex flex-col text-center ml-auto">
                          <span className="text-gray-400 font-medium text-base">{Math.round(bar.stats.divePercentage)}%</span>
                          <span className="text-gray-600 text-[10px] uppercase tracking-wide">Dive</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        )}

        {/* All Bars Tab: Empty results */}
        {activeTab === 'all-bars' && displayedBars.length === 0 && (
          <div className="text-center py-12">
            <img src="/logo-icon.png" alt="" className="w-16 h-16 object-contain mx-auto mb-4 opacity-50" />
            <h3 className="text-white text-lg mb-2">No bars found</h3>
            <p className="text-gray-400 text-sm mb-4">
              {notTriedOnly && user
                ? "You've tried all the bars matching your filters!"
                : "Try adjusting your search or filters"
              }
            </p>
            {activeFiltersCount > 0 && (
              <button
                onClick={clearFilters}
                className="btn-secondary"
              >
                Clear Filters
              </button>
            )}
          </div>
        )}

        {/* All Bars Tab: Show bars */}
        {activeTab === 'all-bars' && displayedBars.length > 0 && (
          <div className="grid gap-4">
            {displayedBars.map(bar => {
              const stats = getBarStats(bar.id);
              const isDive = stats.divePercentage > 50;
              const isRated = hasUserRated(bar.id);
              const myRating = isRated ? getUserRating(bar.id) : null;
              
              return (
                <div 
                  key={bar.id}
                  className={`${highlightRated && isRated ? 'ring-2 ring-amber-500 ring-offset-2 ring-offset-dive-black rounded-xl' : ''}`}
                >
                  <BarCard 
                    bar={bar} 
                    showIndicators={true}
                    isDive={isDive}
                    isRated={isRated}
                    myRating={myRating?.overall}
                  />
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

export default ListView;
